import { Platform } from 'react-native';

export interface UrlObjectIdEntry {
	id: string;
	url: string;
	timestamp: string;
	objectIds: string[]; // Array of ObjectIds found in the URL
	pathname: string;
	search: string;
	hash: string;
	sessionStartTime?: string; // ISO timestamp when user entered the page
	sessionEndTime?: string; // ISO timestamp when user left the page
	durationMs?: number; // Duration in milliseconds
	totalDurationMs?: number; // Total duration across multiple visits (for same URL)
	visitCount?: number; // Number of times this URL was visited
	metadata?: Record<string, any>;
}

/**
 * MongoDB ObjectId validation regex
 * ObjectIds are 24 hexadecimal characters
 */
const OBJECT_ID_REGEX = /[0-9a-fA-F]{24}/g;

/**
 * Check if a string is a valid MongoDB ObjectId
 */
function isValidObjectId(str: string): boolean {
	return /^[0-9a-fA-F]{24}$/.test(str);
}

/**
 * Extract all ObjectIds from a URL
 */
function extractObjectIds(url: string): string[] {
	const matches = url.match(OBJECT_ID_REGEX);
	if (!matches) return [];

	// Filter to only include valid ObjectIds (exact 24 chars)
	const validObjectIds = matches.filter((match) => {
		// Extract the exact match (24 hex chars) from the URL
		// This prevents matching parts of longer hex strings
		return isValidObjectId(match);
	});

	// Remove duplicates
	return Array.from(new Set(validObjectIds));
}

/**
 * URL ObjectId Tracker Service
 * Tracks and stores URLs that contain MongoDB ObjectIds in IndexedDB
 */
interface ActiveSession {
	entryId: string;
	url: string;
	startTime: number; // Timestamp in milliseconds
	objectIds: string[];
}

class UrlObjectIdTracker {
	private dbInstance: IDBDatabase | null = null;
	private readonly dbName = 'UrlObjectIdDB';
	private readonly dbVersion = 2; // Incremented for duration tracking
	private readonly storeName = 'urls';
	private isInitialized = false;
	private activeSession: ActiveSession | null = null;
	private visibilityChangeHandler: (() => void) | null = null;
	private beforeUnloadHandler: (() => void) | null = null;

	/**
	 * Initialize IndexedDB database
	 */
	async initDB(): Promise<IDBDatabase> {
		if (this.dbInstance && this.dbInstance.version > 0) {
			return this.dbInstance;
		}

		if (Platform.OS !== 'web') {
			throw new Error('IndexedDB only available on web platform');
		}

		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, this.dbVersion);

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				const transaction = (event.target as IDBOpenDBRequest)
					.transaction;

				if (!db.objectStoreNames.contains(this.storeName)) {
					// Create new object store
					const store = db.createObjectStore(this.storeName, {
						keyPath: 'id',
						autoIncrement: false,
					});

					store.createIndex('timestamp', 'timestamp', {
						unique: false,
					});
					store.createIndex('url', 'url', { unique: false });
					store.createIndex('objectIds', 'objectIds', {
						unique: false,
						multiEntry: true,
					});
					store.createIndex('sessionStartTime', 'sessionStartTime', {
						unique: false,
					});
				} else {
					// Database exists, check if we need to add new indexes
					if (transaction) {
						const store = transaction.objectStore(this.storeName);
						if (!store.indexNames.contains('sessionStartTime')) {
							store.createIndex(
								'sessionStartTime',
								'sessionStartTime',
								{
									unique: false,
								}
							);
						}
					}
				}
			};

			request.onsuccess = (event) => {
				this.dbInstance = (event.target as IDBOpenDBRequest).result;
				this.isInitialized = true;
				resolve(this.dbInstance);
			};

			request.onerror = (event) => {
				reject((event.target as IDBOpenDBRequest).error);
			};
		});
	}

	/**
	 * End the current active session and update duration
	 */
	private async endActiveSession(): Promise<void> {
		if (!this.activeSession) return;

		const endTime = Date.now();
		const duration = endTime - this.activeSession.startTime;

		try {
			const db = await this.initDB();
			const transaction = db.transaction(this.storeName, 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const request = store.get(this.activeSession.entryId);

			request.onsuccess = () => {
				const entry = request.result as UrlObjectIdEntry;
				if (entry) {
					const previousDuration = entry.totalDurationMs || 0;
					const previousVisits = entry.visitCount || 0;

					// Update entry with session end time and duration
					const updatedEntry: UrlObjectIdEntry = {
						...entry,
						sessionEndTime: new Date(endTime).toISOString(),
						durationMs: duration,
						totalDurationMs: previousDuration + duration,
						visitCount: previousVisits + 1,
					};

					const updateRequest = store.put(updatedEntry);
					updateRequest.onsuccess = async () => {
						console.log('✅ Updated session duration:', {
							url: entry.url,
							durationMs: duration,
							totalDurationMs: updatedEntry.totalDurationMs,
							visitCount: updatedEntry.visitCount,
						});

						// Track document viewing in context tracker
						try {
							const { addContextEntry } =
								await import('./contextTracker');
							const viewingContent = JSON.stringify({
								url: entry.url,
								pathname: entry.pathname,
								objectIds: entry.objectIds,
								durationMs: duration,
								totalDurationMs: updatedEntry.totalDurationMs,
								visitCount: updatedEntry.visitCount,
								sessionStartTime: entry.sessionStartTime,
								sessionEndTime: updatedEntry.sessionEndTime,
							});
							await addContextEntry(
								'document_viewing',
								viewingContent,
								{
									url: entry.url,
									objectIds: entry.objectIds,
									durationMs: duration,
									totalDurationMs:
										updatedEntry.totalDurationMs,
									visitCount: updatedEntry.visitCount,
									timestamp:
										updatedEntry.sessionEndTime ||
										new Date().toISOString(),
								}
							);
						} catch (error) {
							// Silently fail - context tracking is optional
							if (__DEV__) {
								console.warn(
									'Failed to track document viewing in context:',
									error
								);
							}
						}
					};
					updateRequest.onerror = () => {
						console.error(
							'Failed to update session duration:',
							updateRequest.error
						);
					};
				}
			};

			request.onerror = () => {
				console.error(
					'Failed to get entry for session update:',
					request.error
				);
			};
		} catch (error) {
			console.error('Error ending active session:', error);
		}

		// Clear active session
		this.activeSession = null;
	}

	/**
	 * Setup visibility change handler to pause/resume tracking
	 */
	private setupVisibilityHandlers(): void {
		if (Platform.OS !== 'web' || typeof document === 'undefined') return;

		// Handle visibility change (tab switch, minimize, etc.)
		this.visibilityChangeHandler = () => {
			if (document.hidden) {
				// Tab is hidden, end session but don't clear it (user might come back)
				if (this.activeSession) {
					// Store pause time
					(this.activeSession as any).pauseTime = Date.now();
				}
			} else {
				// Tab is visible again, resume session
				if (
					this.activeSession &&
					(this.activeSession as any).pauseTime
				) {
					// Adjust start time by the pause duration
					const pauseDuration =
						Date.now() - (this.activeSession as any).pauseTime;
					this.activeSession.startTime += pauseDuration;
					delete (this.activeSession as any).pauseTime;
				}
			}
		};

		// Handle beforeunload (page close, navigation away)
		this.beforeUnloadHandler = () => {
			// End session when leaving the page
			this.endActiveSession();
		};

		document.addEventListener(
			'visibilitychange',
			this.visibilityChangeHandler
		);
		window.addEventListener('beforeunload', this.beforeUnloadHandler);
	}

	/**
	 * Cleanup visibility handlers
	 */
	private cleanupVisibilityHandlers(): void {
		if (Platform.OS !== 'web' || typeof document === 'undefined') return;

		if (this.visibilityChangeHandler) {
			document.removeEventListener(
				'visibilitychange',
				this.visibilityChangeHandler
			);
			this.visibilityChangeHandler = null;
		}

		if (this.beforeUnloadHandler) {
			window.removeEventListener(
				'beforeunload',
				this.beforeUnloadHandler
			);
			this.beforeUnloadHandler = null;
		}
	}

	/**
	 * Track a URL if it contains ObjectIds and start session tracking
	 * @param url - The URL to check and potentially store
	 * @param metadata - Optional metadata to store with the URL
	 */
	async trackUrl(
		url: string,
		metadata?: Record<string, any>
	): Promise<boolean> {
		if (Platform.OS !== 'web') return false;

		try {
			// Extract ObjectIds from the URL
			const objectIds = extractObjectIds(url);

			// Only track if URL contains at least one ObjectId
			if (objectIds.length === 0) {
				// End any active session if navigating to a page without ObjectId
				if (this.activeSession) {
					await this.endActiveSession();
				}
				return false;
			}

			// Parse URL components
			let urlObj: URL;
			try {
				urlObj = new URL(url);
			} catch (e) {
				// If URL parsing fails, try with current origin
				if (typeof window !== 'undefined') {
					urlObj = new URL(url, window.location.origin);
				} else {
					console.warn('Failed to parse URL:', url);
					return false;
				}
			}

			// If navigating to the same URL, don't create a new entry
			if (this.activeSession && this.activeSession.url === urlObj.href) {
				return true; // Already tracking this URL
			}

			// End previous session if exists
			if (this.activeSession) {
				await this.endActiveSession();
			}

			// Initialize DB if needed
			const db = await this.initDB();

			// Setup visibility handlers if not already set up
			if (!this.visibilityChangeHandler) {
				this.setupVisibilityHandlers();
			}

			const startTime = Date.now();

			// Check if URL already exists in database
			return new Promise((resolve, reject) => {
				const transaction = db.transaction(this.storeName, 'readwrite');
				const store = transaction.objectStore(this.storeName);
				const urlIndex = store.index('url');
				const urlRequest = urlIndex.getAll(urlObj.href);

				urlRequest.onsuccess = () => {
					const existingEntries = urlRequest.result;
					let entryId: string;
					let entry: UrlObjectIdEntry;

					if (existingEntries.length > 0) {
						// Update existing entry (use most recent one)
						const existingEntry = existingEntries.sort(
							(a, b) =>
								new Date(b.timestamp).getTime() -
								new Date(a.timestamp).getTime()
						)[0];

						entryId = existingEntry.id;
						entry = {
							...existingEntry,
							timestamp: new Date().toISOString(), // Update last visit timestamp
							sessionStartTime: new Date(startTime).toISOString(),
							visitCount: (existingEntry.visitCount || 0) + 1,
							// Keep existing totalDurationMs, will be updated when session ends
						};
					} else {
						// Create new entry
						entryId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
						entry = {
							id: entryId,
							url: urlObj.href,
							timestamp: new Date().toISOString(),
							objectIds,
							pathname: urlObj.pathname,
							search: urlObj.search,
							hash: urlObj.hash,
							sessionStartTime: new Date(startTime).toISOString(),
							visitCount: 1,
							totalDurationMs: 0,
							metadata: {
								...metadata,
								origin: urlObj.origin,
								hostname: urlObj.hostname,
							},
						};
					}

					// Store or update entry
					const putRequest = store.put(entry);
					putRequest.onsuccess = () => {
						// Set as active session
						this.activeSession = {
							entryId,
							url: urlObj.href,
							startTime,
							objectIds,
						};

						console.log(
							'✅ Tracked URL with ObjectIds (session started):',
							{
								url: entry.url,
								objectIds: entry.objectIds,
								sessionStartTime: entry.sessionStartTime,
								visitCount: entry.visitCount,
								isNewEntry: existingEntries.length === 0,
							}
						);
						resolve(true);
					};

					putRequest.onerror = () => {
						console.error('Failed to track URL:', putRequest.error);
						reject(putRequest.error);
					};
				};

				urlRequest.onerror = () => {
					console.error(
						'Failed to query existing URLs:',
						urlRequest.error
					);
					reject(urlRequest.error);
				};
			});
		} catch (error) {
			console.error('Error tracking URL:', error);
			return false;
		}
	}

	/**
	 * Get all tracked URLs
	 * @param limit - Optional limit on number of entries to return
	 */
	async getTrackedUrls(limit?: number): Promise<UrlObjectIdEntry[]> {
		if (Platform.OS !== 'web') return [];

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction(this.storeName, 'readonly');
				const store = transaction.objectStore(this.storeName);
				const index = store.index('timestamp');
				const request = limit
					? index.getAll(null, limit)
					: index.getAll();

				request.onsuccess = () => {
					const results = request.result.reverse(); // Most recent first
					resolve(results);
				};

				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to get tracked URLs:', error);
			return [];
		}
	}

	/**
	 * Get URLs containing a specific ObjectId
	 * @param objectId - The ObjectId to search for
	 */
	async getUrlsByObjectId(objectId: string): Promise<UrlObjectIdEntry[]> {
		if (Platform.OS !== 'web') return [];

		if (!isValidObjectId(objectId)) {
			console.warn('Invalid ObjectId:', objectId);
			return [];
		}

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction(this.storeName, 'readonly');
				const store = transaction.objectStore(this.storeName);
				const index = store.index('objectIds');
				const request = index.getAll(objectId);

				request.onsuccess = () => {
					const results = request.result.reverse(); // Most recent first
					resolve(results);
				};

				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to get URLs by ObjectId:', error);
			return [];
		}
	}

	/**
	 * Clear all tracked URLs
	 */
	async clearTrackedUrls(): Promise<void> {
		if (Platform.OS !== 'web') return;

		// End active session before clearing
		if (this.activeSession) {
			await this.endActiveSession();
		}

		// Cleanup handlers
		this.cleanupVisibilityHandlers();

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction(this.storeName, 'readwrite');
				const store = transaction.objectStore(this.storeName);
				const request = store.clear();

				request.onsuccess = () => resolve();
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to clear tracked URLs:', error);
		}
	}

	/**
	 * Manually end the current session (useful for testing or manual control)
	 */
	async endCurrentSession(): Promise<void> {
		await this.endActiveSession();
	}

	/**
	 * Get count of tracked URLs
	 */
	async getTrackedUrlCount(): Promise<number> {
		if (Platform.OS !== 'web') return 0;

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction(this.storeName, 'readonly');
				const store = transaction.objectStore(this.storeName);
				const request = store.count();

				request.onsuccess = () => resolve(request.result);
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to get tracked URL count:', error);
			return 0;
		}
	}
}

// Create singleton instance
const urlObjectIdTracker = new UrlObjectIdTracker();

// Export functions
export const trackUrlWithObjectId = (
	url: string,
	metadata?: Record<string, any>
) => urlObjectIdTracker.trackUrl(url, metadata);

export const getTrackedUrls = (limit?: number) =>
	urlObjectIdTracker.getTrackedUrls(limit);

export const getUrlsByObjectId = (objectId: string) =>
	urlObjectIdTracker.getUrlsByObjectId(objectId);

export const clearTrackedUrls = () => urlObjectIdTracker.clearTrackedUrls();

export const getTrackedUrlCount = () => urlObjectIdTracker.getTrackedUrlCount();

export const endCurrentSession = () => urlObjectIdTracker.endCurrentSession();

export default urlObjectIdTracker;
